import Button from "components/ui/Button";
import { ChevronLeft, ChevronRight, Download, X } from "lucide-react";
import { useState, useEffect } from "react";
import React from "react";

interface SalesProperty {
	id: string;
	title: string;
	address: string;
	type: string;
	price: number;
	status: string;
	bedrooms: number;
	bathrooms: number;
	square_feet: number;
	images: string[];
	description: string;
	created_at: string;
	viewings: number;
	inquiries: number;
}

interface PropertyImageGalleryProps {
	property: SalesProperty | null;
	onClose: () => void;
}

const PropertyImageGallery: React.FC<PropertyImageGalleryProps> = ({ property, onClose }) => {
	const [currentImageIndex, setCurrentImageIndex] = useState(0);
	// Filter out webp images and invalid URLs, allow jpg, jpeg, png
	const images = (property?.images || []).filter((img) => {
		if (!img) return false;
		// Filter out webp format explicitly
		if (img.toLowerCase().includes('.webp')) return false;
		// Only include valid full URLs or data URLs
		const isValidUrl = img.startsWith("http://") || img.startsWith("https://") || img.startsWith("data:");
		if (!isValidUrl) return false;
		// For data URLs, check if it's jpg, jpeg, or png
		if (img.startsWith("data:")) {
			return img.startsWith("data:image/jpeg") ||
			       img.startsWith("data:image/jpg") ||
			       img.startsWith("data:image/png");
		}
		// For regular URLs, allow if it doesn't contain .webp (jpg, jpeg, png are allowed)
		return true;
	});

	const nextImage = () => {
		setCurrentImageIndex((prev) => (prev + 1) % images?.length);
	};

	const prevImage = () => {
		setCurrentImageIndex(
			(prev) => (prev - 1 + images?.length) % images?.length,
		);
	};

	const downloadImage = (imageUrl: string, index: number) => {
		const link = document.createElement("a");
		link.href = imageUrl;
		link.download = `property-${property?.id}-image-${index + 1}.jpg`;
		document.body?.appendChild(link);
		link?.click();
		document.body?.removeChild(link);
	};

	// Handle ESC key to close
	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
			}
		};
		window.addEventListener("keydown", handleEscape);
		return () => window.removeEventListener("keydown", handleEscape);
	}, [onClose]);

	if (!images?.length) {
		return (
			<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
				<div className="bg-card rounded-lg border border-border p-8 max-w-md w-full mx-4">
					<div className="flex items-center justify-between mb-6">
						<h2 className="text-xl font-semibold text-foreground">
							Property Images
						</h2>
						<Button variant="ghost" size="icon" onClick={onClose}>
							<X className="w-5 h-5" />
						</Button>
					</div>

					<div className="text-center py-8">
						<div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
							<X className="w-6 h-6 text-muted-foreground" />
						</div>
						<h3 className="text-lg font-semibold text-foreground mb-2">
							No Images Available
						</h3>
						<p className="text-muted-foreground">
							This property doesn't have any images uploaded yet.
						</p>
					</div>

					<div className="flex justify-end mt-6">
						<Button onClick={onClose}>Close</Button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
			<div className="relative w-full h-full flex items-center justify-center p-4">
				{/* Close Button - Icon */}
				<Button
					variant="ghost"
					size="icon"
					onClick={onClose}
					className="absolute top-4 right-4 z-10 bg-black/50 text-white hover:bg-black/70"
					title="Close (ESC)"
				>
					<X className="w-6 h-6" />
				</Button>

				{/* Close Button - Text */}
				<Button
					variant="outline"
					onClick={onClose}
					className="absolute top-4 right-20 z-10 bg-black/50 text-white border-white/30 hover:bg-black/70 hover:border-white/50"
				>
					Close
				</Button>

				{/* Property Info */}
				<div className="absolute top-4 left-4 z-10 bg-black/50 text-white rounded-lg p-4 max-w-md">
					<h2 className="text-xl font-semibold mb-1">{property?.title}</h2>
					<p className="text-sm opacity-90">{property?.address}</p>
					<p className="text-lg font-bold mt-2">
						{new Intl.NumberFormat("en-NG", {
							style: "currency",
							currency: "NGN",
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						})?.format(Number(property?.price) || 0)}
					</p>
				</div>

				{/* Image Navigation - Previous */}
				{images?.length > 1 && (
					<Button
						variant="ghost"
						size="icon"
						onClick={prevImage}
						className="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white hover:bg-black/70"
					>
						<ChevronLeft className="w-8 h-8" />
					</Button>
				)}

				{/* Main Image */}
				<div className="relative max-w-6xl max-h-[80vh] flex items-center justify-center">
					<img
						src={images?.[currentImageIndex]}
						alt={`${property?.title} - Image ${currentImageIndex + 1}`}
						className="max-w-full max-h-full object-contain rounded-lg"
					/>

					{/* Image Actions */}
					<div className="absolute bottom-4 right-4 flex items-center gap-2">
						<Button
							variant="ghost"
							size="icon"
							onClick={() =>
								downloadImage(images?.[currentImageIndex], currentImageIndex)
							}
							className="bg-black/50 text-white hover:bg-black/70"
							title="Download Image"
						>
							<Download className="w-5 h-5" />
						</Button>
					</div>
				</div>

				{/* Image Navigation - Next */}
				{images?.length > 1 && (
					<Button
						variant="ghost"
						size="icon"
						onClick={nextImage}
						className="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 bg-black/50 text-white hover:bg-black/70"
					>
						<ChevronRight className="w-8 h-8" />
					</Button>
				)}

				{/* Image Counter */}
				{images?.length > 1 && (
					<div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 bg-black/50 text-white rounded-lg px-4 py-2">
						<span className="text-sm">
							{currentImageIndex + 1} of {images?.length}
						</span>
					</div>
				)}

				{/* Thumbnail Navigation */}
				{images?.length > 1 && (
					<div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-10">
						<div className="flex items-center gap-2 bg-black/50 rounded-lg p-2 max-w-md overflow-x-auto">
							{images?.map((image, index) => (
								<button
									key={index}
									onClick={() => setCurrentImageIndex(index)}
									className={`flex-shrink-0 w-12 h-12 rounded border-2 overflow-hidden transition-all ${
										index === currentImageIndex
											? "border-white"
											: "border-transparent opacity-70 hover:opacity-100"
									}`}
								>
									<img
										src={image}
										alt={`Thumbnail ${index + 1}`}
										className="w-full h-full object-cover"
									/>
								</button>
							))}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default PropertyImageGallery;
